import { useEffect, useRef, useState } from "react";

import OpenLayersMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorTileLayer from "ol/layer/VectorTile";

import { useGeographic } from "ol/proj";
import { applyStyle } from "ol-mapbox-style";
import { useUserAuth } from "../contexts/authContext";
import { useMap as useMapContext } from "../contexts/mapContext";

export default function useMap(mapID, rerender) {
    const { userMapSettings, userProfileSettings, updateProfileSettings } = useUserAuth();
    const { mapTileOptions } = useMapContext();

    const [mapLocation, setMapLocation] = useState(userProfileSettings.location);
    const [map, setMap] = useState(null);

    const settingsRef = useRef(userMapSettings);

    useGeographic();

    useEffect(() => {
        initializeMap();
    }, [rerender]);

    // store map location in the browser
    useEffect(() => {
        if (!map) {
            return;
        }

        let debounceTimer;
        const handleViewChange = () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            debounceTimer = setTimeout(() => {
                const view = map.getView();
                setMapLocation({ center: view.getCenter(), zoom: view.getZoom() });
            }, 500);
        };

        map.getView().on("change:center", handleViewChange);
        map.getView().on("change:resolution", handleViewChange);

        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            map.getView().un("change:center", handleViewChange);
            map.getView().un("change:resolution", handleViewChange);

            // Clean up and dispose of Map.
            map.setTarget(null);
            map.getLayers().forEach((layer) => {
                const source = layer.getSource();
                if (source) {
                    source.dispose?.();
                    source.clear?.(true);
                }
                layer.dispose();
            });
            map.getLayers().clear();
        };
    }, [map]);

    useEffect(() => {
        if (!map) return;

        const layers = map.getLayers().getArray();
        layers.forEach((layer) => {
            const layerName = layer.get("name");
            if (layerName !== "base") return;

            map.removeLayer(layer);
            let newLayer = undefined;

            const baseLayerSettings = mapTileOptions.find(({ index }) => {
                return userMapSettings.map_preference === index;
            });
            if (baseLayerSettings && baseLayerSettings.style_url) {
                newLayer = new VectorTileLayer({
                    declutter: true,
                    opacity: settingsRef.current.brightness,
                    properties: { name: "base" },
                    zIndex: 0
                });
                applyStyle(newLayer, baseLayerSettings.style_url);
            } else if (baseLayerSettings && baseLayerSettings.source) {
                newLayer = new TileLayer({
                    source: baseLayerSettings.source,
                    opacity: settingsRef.current.brightness,
                    properties: { name: "base" },
                    zIndex: 0
                });
                if (userMapSettings.map_preference === 1) {
                    newLayer.on("prerender", (evt) => {
                        if (evt.context) {
                            const context = evt.context;
                            context.globalCompositeOperation = "source-over";
                            context.filter = "grayscale(80%) invert(100%)";
                        }
                    });
                    newLayer.on("postrender", (evt) => {
                        if (evt.context) {
                            const context = evt.context;
                            context.filter = "none";
                        }
                    });
                }
            }
            if (newLayer) {
                map.addLayer(newLayer);
            }
            map.add;
        });
    }, [userMapSettings]);

    useEffect(() => {
        const updatedProfileSettings = {
            ...userProfileSettings,
            location: mapLocation
        };
        updateProfileSettings(updatedProfileSettings);
    }, [mapLocation]);

    const initializeMap = async () => {
        let selectedTile = mapTileOptions.find(({ index }) => {
            return userMapSettings.map_preference === index;
        });
        let tileSource = null;
        if (selectedTile) {
            tileSource = selectedTile.source;
        } else {
            tileSource = new OSM();
        }
        let tileLayer = null;
        if (selectedTile && selectedTile.style_url) {
            tileLayer = new VectorTileLayer({
                declutter: true,
                opacity: settingsRef.current.brightness,
                properties: { name: "base" },
                zIndex: 0
            });
            await applyStyle(tileLayer, selectedTile.style_url);
        } else {
            tileLayer = new TileLayer({
                source: tileSource,
                opacity: settingsRef.current.brightness,
                properties: { name: "base" },
                zIndex: 0
            });
        }
        const map = new OpenLayersMap({
            target: mapID,
            layers: [tileLayer],
            view: new View({
                center: mapLocation.center,
                zoom: mapLocation.zoom
            }),
            controls: []
        });
        map.getViewport().addEventListener("contextmenu", (e) => e.preventDefault());
        setMap(map);
    };

    return map;
}
